import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  AriaAttributes,
  createContext,
} from 'react';
import classnames from 'classnames';
import { useOnScroll } from '@wix/da-hooks/pkg/useOnScroll';
import { PopupContext } from '../popups/PopupContext';
import { scrollTo } from '../../utils/browser';

import s from './ScrollableContent.scss';

const THROTTLE_TIMEOUT = 200;

export interface Props {
  id?: string;
  role?: string;
  className?: string;
  wrapperClassName?: string;
  styles?: React.CSSProperties;
  children: React.ReactNode | React.ReactNode[];
  throttleTimeout?: number;
  scrolledToBottom?: boolean; // scroll to the bottom of the content on mount
  innerRef?: (node: HTMLElement | null) => void;
  onScrolled?: (scrollTop: number, distanceToBottom: number) => void;
  onNotScrollable?: () => void; // to communicate that it's not really scrollable
  'aria-hidden'?: AriaAttributes['aria-hidden'];
}

export const ScrollableContentContext = createContext<
  React.MutableRefObject<HTMLDivElement | null> | undefined
>(undefined);

const ScrollableContent: React.FC<Props> = ({
  id,
  role,
  wrapperClassName,
  styles,
  className,
  children,
  throttleTimeout = THROTTLE_TIMEOUT,
  onScrolled,
  onNotScrollable,
  scrolledToBottom = false,
  innerRef,
  'aria-hidden': ariaHidden,
}) => {
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const { setScrollableContentRef } = useContext(PopupContext);
  const [isScrolled, setScrolled] = useState(false);

  const handleScroll = useCallback(
    (scrollTop, distanceToBottom) => {
      onScrolled && onScrolled(scrollTop, distanceToBottom);
      const scrolled = scrollTop > 0;
      if (isScrolled !== scrolled) {
        setScrolled(scrolled);
      }
    },
    [onScrolled, isScrolled]
  );

  useEffect(() => {
    const contentNode = contentRef.current;
    if (contentNode && scrolledToBottom) {
      scrollTo(contentNode, 0, contentNode.scrollHeight);
    }
  }, [scrolledToBottom]);

  useEffect(() => {
    const contentNode = contentRef.current;
    if (!contentNode) {
      return;
    }
    if (contentNode.clientHeight === contentNode.scrollHeight) {
      onNotScrollable && onNotScrollable();
    }
  }, [children, onNotScrollable]);

  useOnScroll(contentRef, handleScroll, throttleTimeout, true);

  const setRef = (node: HTMLDivElement | null) => {
    contentRef.current = node;
    innerRef && innerRef(node);
    setScrollableContentRef(node);
  };

  return (
    <div
      id={id}
      role={role}
      className={classnames(s['root'], wrapperClassName)}
      aria-hidden={ariaHidden}
    >
      <ScrollableContentContext.Provider value={contentRef}>
        <div style={styles} className={classnames(s['content'], className)} ref={setRef}>
          {children}
        </div>
      </ScrollableContentContext.Provider>
    </div>
  );
};
ScrollableContent.displayName = 'ScrollableContent';

export default ScrollableContent;
